import { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';

import { showApolloErrorToUser, useInput } from '@pletly/ui';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useLoadingOverlay } from '%context';
import { RootStackParamList } from '%navigation/types';
import { useInviteCaregiversToConnectMutation } from './__generated__/inviteCaregivers.generated';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

export const useInviteToConnect = ({
	userUuid,
	onSuccess,
}: {
	userUuid: string;
	onSuccess: () => void;
}) => {
	const { replace, goBack } = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const { showLoadingOverlay, closeLoadingOverlay } = useLoadingOverlay();
	const emailInput = useInput();
	const [emails, setEmails] = useState<string[]>([]);
	const [inviteCaregivers] = useInviteCaregiversToConnectMutation();

	const goToHome = useCallback(() => replace('Home'), [replace]);

	const handleSendPress = useCallback(async () => {
		showLoadingOverlay();

		try {
			await inviteCaregivers({ variables: { careReceiverUuid: userUuid, emails } });

			return onSuccess();
		} catch (error) {
			showApolloErrorToUser(error as Error);
		} finally {
			closeLoadingOverlay();
		}
	}, [showLoadingOverlay, inviteCaregivers, userUuid, emails, onSuccess, closeLoadingOverlay]);

	const handleAddEmail = () => {
		const isValidEmail = EMAIL_REGEX.test(emailInput.value);
		if (!isValidEmail) {
			return;
		}

		const emailLowerCase = emailInput.value.toLowerCase();

		if (emails.includes(emailLowerCase)) {
			return emailInput.reset();
		}
		setEmails((prevEmails) => [...prevEmails, emailLowerCase]);
		emailInput.reset();
	};

	const handleRemoveEmail = (email: string) => () => {
		setEmails((prevEmails) => prevEmails.filter((prevEmail) => prevEmail !== email));
	};

	return {
		handleAddEmail,
		handleRemoveEmail,
		handleSendPress,
		emailInput,
		emails,
		goToHome,
		goBack,
	};
};
